// ============================================================================
// DeleteAccountModal — modale de confirmation de suppression de compte.
// ============================================================================
// Requis par Apple Guideline 5.1.1(v) : suppression IN-APP + double
// confirmation (typer « SUPPRIMER » — cf. `delete-account-confirm.ts`).
//
// Comportement :
//   1. Warning + liste des données supprimées.
//   2. Avertissement spécifique iOS : si un abonnement IAP est actif, il faut
//      le résilier séparément dans Réglages iOS (Apple n'autorise pas
//      l'annulation programmatique d'un IAP par une app tierce).
//   3. Champ pour taper « SUPPRIMER » — le bouton reste désactivé sinon.
//   4. Sur succès : clear cache + signOut + redirect /login.
//
// Focus trap volontairement minimaliste : on ne monte qu'un contenu, la modale
// est bloquante (fermable seulement par « Annuler » ou la croix), et on
// autofocus le champ de confirmation.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import { isIOSNative, isNativeApp } from '@/lib/platform';
import { DELETE_ACCOUNT_CONFIRM_WORD, isDeletionConfirmed } from './delete-account-confirm';
import { useDeleteAccount } from './use-delete-account';

interface DeleteAccountModalProps {
  onClose: () => void;
}

export function DeleteAccountModal({ onClose }: DeleteAccountModalProps) {
  const [confirmInput, setConfirmInput] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mutation = useDeleteAccount();

  const isNative = isNativeApp();
  const isIOS = isIOSNative();

  useEffect(() => {
    // Autofocus le champ + block scroll body pendant l'ouverture
    inputRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const canConfirm = isDeletionConfirmed(confirmInput) && !mutation.isPending;

  async function handleConfirm() {
    if (!canConfirm) return;
    setErrorMsg(null);
    try {
      await mutation.mutateAsync();
      // Succès : clear cache + signout + redirect
      queryClient.clear();
      await supabase.auth.signOut();
      navigate({ to: '/login', search: { deleted: 1 } as never });
    } catch (err) {
      setErrorMsg(
        err instanceof Error
          ? err.message
          : 'La suppression a échoué. Réessaie ou contacte support@jeraisonne.fr.',
      );
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !mutation.isPending) onClose();
      }}
    >
      <div className="w-full max-w-md overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <h2 id="delete-account-title" className="text-lg font-bold text-gray-900">
              Supprimer mon compte
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="rounded-xl p-2 text-gray-500 transition-colors hover:bg-gray-100 disabled:opacity-40"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          <p className="text-sm text-gray-700">
            Cette action est <strong className="font-semibold">définitive et irréversible</strong>.
            Ton compte et les données associées seront supprimés immédiatement.
          </p>

          <div className="mt-3 rounded-xl bg-gray-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Données supprimées
            </p>
            <ul className="mt-2 space-y-1 text-xs text-gray-600">
              <li>• Ton profil, ton email, ton avatar</li>
              <li>• Ta progression, tes résultats, ton historique d'exercices</li>
              <li>• Tes objectifs, tes classes et liens tuteurs/parents</li>
              <li>• Tes notifications, ton XP, ton IR</li>
            </ul>
            <p className="mt-2 text-xs text-gray-500">
              Les contenus publics que tu as créés (exercices, thèmes, articles) restent en ligne de
              façon anonymisée.
            </p>
          </div>

          {isIOS && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-800">Abonnement App Store</p>
              <p className="mt-1 text-xs text-amber-700">
                Si tu as un abonnement souscrit via l'App Store, tu dois le résilier séparément dans{' '}
                <strong>Réglages iOS → Ton nom → Abonnements</strong>. La suppression du compte ici
                ne l'annule pas.
              </p>
            </div>
          )}

          {isNative && !isIOS && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-800">Abonnement Play Store</p>
              <p className="mt-1 text-xs text-amber-700">
                Si tu as un abonnement souscrit via Google Play, résilie-le dans{' '}
                <strong>Play Store → Paiements et abonnements → Abonnements</strong>.
              </p>
            </div>
          )}

          <div className="mt-4">
            <label htmlFor="delete-confirm" className="block text-sm font-medium text-gray-900">
              Tape <span className="font-mono font-semibold">{DELETE_ACCOUNT_CONFIRM_WORD}</span>{' '}
              pour confirmer
            </label>
            <input
              ref={inputRef}
              id="delete-confirm"
              type="text"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              autoCapitalize="characters"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder={DELETE_ACCOUNT_CONFIRM_WORD}
              disabled={mutation.isPending}
              className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-3 font-mono text-sm outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 disabled:bg-gray-50"
            />
          </div>

          {errorMsg && (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3">
              <p className="text-xs font-semibold text-red-800">Erreur</p>
              <p className="mt-1 text-xs text-red-700">{errorMsg}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse gap-2 border-t border-gray-100 px-5 py-4 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
            className="w-full sm:w-auto"
          >
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="w-full bg-red-600 text-white hover:bg-red-700 sm:w-auto"
          >
            {mutation.isPending ? 'Suppression…' : 'Supprimer définitivement'}
          </Button>
        </div>
      </div>
    </div>
  );
}
